import { Fragment, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Button,
  ConfidenceTicks,
  confidenceBand,
  Divider,
  Group,
  InsightCard,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Tag,
  Text,
  type ConfidenceThresholds,
} from '@grovus/design-system'
import { isTopmostOverlay, OVERLAY_LAYER, registerOverlay, unregisterOverlay } from '../components/overlayRegistry'
import type { Dict, Lang } from '../i18n'
import {
  CreativeDetailUnknownError,
  CreativesRequestError,
  fetchCreativeDetail,
  type CreativesParams,
  REQUEST_TIMEOUT_MS,
} from './api'
import type { ContractVersion, CreativeDetailResponse, MembershipReason } from './contract'
import { matchesEcho, periodKey } from '../lib/period'
import { fill, money, moneyParts, NO_VALUE, num, percent, ratio } from './format'
import type { SpendShare } from './metrics'
import { adjustedPartial, derive } from './metrics'
import { thresholdsOf } from './confidence'
import { buildRows } from './partition'
import type { Row } from './partition'
import {
  verdictQualifier,
  qualifierText,
  labelOf,
  markTally,
  measureNames,
  formatLabel,
  PartialLegend,
  PausedMark,
  reasonSentence,
  referenceNoun,
  metricName,
  verdictMetric,
  floorStruck,
  VerdictMark,
  type ViewContext,
} from './view'

const OVERLAY_ID = 'creative-detail-panel'

/** The unit after a video's duration in seconds. */
const SECONDS: Record<Lang, string> = { en: 's', ru: ' с', uk: ' с' }

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; response: CreativeDetailResponse }
  | { status: 'unknown' }
  | { status: 'error'; message: string }

export interface CreativeDetailPanelProps {
  /** The creative group id; the identity, and the path segment of the detail request. */
  id: string
  /** The screen's context: language, thresholds, and the dimension lists the labels come
   * from. Benchmarks and currency do not come from here: they travel in the detail
   * response, so the verdict and the reliability copy compute within one vintage. */
  ctx: ViewContext
  /** The opening row's share of the filtered segment, a list-snapshot fact. */
  share: SpendShare
  /** The request scope the panel re-asks with: the screen's company, period, accounts and audience. */
  params: CreativesParams
  /** The dates the list the panel opened from covers. A preset resolved on either side of
   * midnight echoes the same preset over another window, so the panel renders only a
   * response over these exact dates. */
  listWindow?: { from: string; to: string }
  /** The contract the list's body followed. Across an upgrade or a rollback the two ends of
   * a window do not say which response is newer, so a version that differs refreshes the
   * list instead. */
  listContract?: ContractVersion
  /** The list is still being asked for the panel's own period or scope. Its window is then
   * the old one, and the panel waits for the answer instead of reconciling against it. */
  listPending?: boolean
  /** The panel's window moved past the list's: the list refetches, and the panel waits. */
  onStaleList?: () => void
  /** A body from the pre-GRO-65 server: the shell falls back to the old contract. */
  onOldContract?: () => void
  onClose: () => void
  /** Injected in tests; the real panel uses the global. */
  /** The screen's `reloadKey`: a guest re-read issues the detail request again. */
  reloadKey?: number
  /** The screens are hidden, not unmounted (GRO-83): the panel keeps its creative but
   * leaves the overlay stack and answers no key until it is shown again. */
  suspended?: boolean
  fetchImpl?: typeof fetch
}

/**
 * The detail side panel, opened from a row or a card on the creatives screen.
 *
 * It fetches and owns its own load states, the way the screen owns its list fetch. It
 * renders only a response that answers the request it sent - the `scope` and `period`
 * echoes must both match, because the scope object alone holds no period and a
 * 14-to-30-day change would leave it untouched while the figures changed - and it aborts
 * superseded requests, so an earlier response can never land last.
 *
 * The overlay contract, for the reason GRO-20 recorded and this design extends: the panel
 * is a non-modal `aside` with no `aria-modal` and no `dialog` role, because a boundary
 * announced as modal without containment is a lie. Its Tab handling cycles between the
 * two regions that sit above the backdrop - the panel's own controls, then the shell
 * header and its disclosures - so keyboard focus never rests on a dimmed control, while
 * the dimmed content stays pointer-reachable exactly as the dismiss gesture. Only the
 * topmost overlay answers Escape, so a header disclosure opened over the panel closes
 * first and the panel survives it.
 */
export function CreativeDetailPanel({
  id,
  ctx,
  share,
  params,
  listWindow,
  listContract,
  listPending = false,
  onStaleList,
  onOldContract,
  onClose,
  reloadKey = 0,
  suspended = false,
  fetchImpl,
}: CreativeDetailPanelProps) {
  const t = ctx.t
  const panelRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const suspendedRef = useRef(suspended)
  suspendedRef.current = suspended
  // Was this close a dismissal the reader asked for, or did the panel close by itself?
  // Only the three dismissal routes set it. Where focus happens to sit cannot answer the
  // question: the header is inside the panel's own Tab cycle, so a reader who moves into
  // a header disclosure and then presses Escape is dismissing the panel from outside it.
  const dismissedRef = useRef(false)
  // The element the panel will hand focus back to, captured once for the panel's whole
  // life. It cannot be read afresh on every effect setup: StrictMode replays the effect
  // in development, and by the replay the first setup has already moved focus to the
  // close button, so a second reading records that button and a later dismissal falls to
  // the heading with the opening row still sitting in the document.
  const triggerRef = useRef<HTMLElement | null>(null)
  const triggerCapturedRef = useRef(false)
  function dismiss() {
    dismissedRef.current = true
    onCloseRef.current()
  }

  const [state, setState] = useState<DetailState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const accountKey = params.accountIds === null ? null : params.accountIds.join(',')

  // The fetch effect aborts superseded requests on its dependency key and discards a
  // response that arrives after the panel moved on, so rapid selection or checkbox
  // changes cannot let an earlier response land last.
  useEffect(() => {
    const controller = new AbortController()
    setState({ status: 'loading' })
    // Composing, not overriding: the controller's cancellation must not retire the
    // request deadline, and the deadline must not retire the cancellation.
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)])
    fetchCreativeDetail(id, params, fetchImpl ?? fetch, signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setState({ status: 'ready', response })
      })
      .catch((error: unknown) => {
        // Only the panel's own cancellation is silent: a deadline or a session failure
        // renders the retry path, which is where a stalled request belongs too.
        if (controller.signal.aborted) return
        if (error instanceof CreativeDetailUnknownError) {
          setState({ status: 'unknown' })
          return
        }
        // The list's `asOf` aged out: the list refetches, and its new `asOf` reaches this
        // request through the key. The panel stays in its loading state meanwhile.
        if (error instanceof CreativesRequestError && error.code === 'as_of_out_of_range') {
          reports.current.onStaleList?.()
          return
        }
        // A preset refused is one the server does not know: a backend rolled back under a
        // 7-day view. The shell falls back to the old contract and selects 14 days, which
        // changes the params and refetches; retrying the same request would only repeat it.
        if (error instanceof CreativesRequestError && error.code === 'bad_period' && params.period.kind === 'preset') {
          reports.current.onOldContract?.()
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : String(error),
        })
      })
    return () => controller.abort()
    // `accountKey` and not the array: the screen holds the selection as state, but the
    // same set of accounts is the same request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, params.companyId, periodKey(params.period), accountKey, params.audienceId, params.asOf, attempt, reloadKey, fetchImpl])

  // A body from the old contract is reported up so the shell falls back, and is rendered
  // by the same echo rule: the old server answers a preset the new client can still ask
  // for, so its figures stay usable across a mixed deploy.
  //
  // A preset resolved on either side of midnight lands the panel and the list on different
  // windows. Whichever answered the earlier day refetches: the list through the screen,
  // the panel by itself. The side on the later day is the current one.
  //
  // Across an upgrade or a rollback the windows cannot be compared at all: an old preset
  // ends today and a current one yesterday, so the ends say nothing about which is newer,
  // and neither does the contract. A contract that differs from the list's refreshes the
  // list first; a fresh list that still differs means the server is now what the list
  // says, so the panel refetches itself.
  //
  // Reconciling is bounded: two moves per request, after which a mismatch that persists is
  // an error the reader can retry, never a loop of requests.
  const ready = state.status === 'ready' ? state.response : null
  const ask = { period: params.period, compare: false }
  const oldBody = ready !== null && ready.contract === 'old'
  const versionsDiffer = ready !== null && !listPending && listContract !== undefined && ready.contract !== listContract
  const datesDiffer =
    ready !== null
    && !listPending
    && !versionsDiffer
    && listWindow !== undefined
    && matchesEcho(ask, { period: ready.period, comparison: null })
    && !matchesEcho(ask, { period: ready.period, comparison: null }, listWindow)
  const listOlder = versionsDiffer || (datesDiffer && ready.period.to > listWindow.to)
  const panelOlder = datesDiffer && ready.period.to < listWindow.to
  const reports = useRef({ onStaleList, onOldContract })
  reports.current = { onStaleList, onOldContract }
  // The list's window is part of what is being reconciled against, so a new one starts
  // the count afresh.
  const requestKey = `${id}|${periodKey(params.period)}|${accountKey}|${params.audienceId}|${params.asOf}|${listWindow?.from}|${listWindow?.to}`
  const reconciled = useRef({ key: requestKey, moves: 0, listRefreshed: false })
  if (reconciled.current.key !== requestKey) reconciled.current = { key: requestKey, moves: 0, listRefreshed: false }
  const reconcile = (move: () => void) => {
    if (reconciled.current.moves >= 2) {
      setState({ status: 'error', message: t.detailWindowMismatch })
      return
    }
    reconciled.current.moves += 1
    move()
  }
  useEffect(() => {
    if (oldBody) reports.current.onOldContract?.()
  }, [oldBody])
  // A mismatch error belongs to the list window it was reached against: when the list
  // answers anew, the panel asks again.
  const mismatched = state.status === 'error' && state.message === t.detailWindowMismatch
  useEffect(() => {
    if (mismatched) setAttempt((n) => n + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listWindow?.from, listWindow?.to, listPending])
  // Keyed on the response as well as the verdict: a fast refetch can land in the same
  // batch as its loading state, and the verdict alone would then never change back.
  useEffect(() => {
    if (!listOlder) return
    if (versionsDiffer && reconciled.current.listRefreshed) {
      reconcile(() => setAttempt((n) => n + 1))
      return
    }
    reconcile(() => {
      reconciled.current.listRefreshed = true
      reports.current.onStaleList?.()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOlder, ready])
  useEffect(() => {
    if (panelOlder) reconcile(() => setAttempt((n) => n + 1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOlder, ready])

  // Overlay registration, Escape and the Tab cycle. `onClose` comes through a ref so the
  // listeners bind once; the panel is one instance for one creative.
  //
  // The overlays are fixed to the viewport, not positioned inside the shell body. An
  // overlay whose containing block scrolls travels with the content, and below 768px the
  // body is the scroller, so scrolling the table carried the panel and the backdrop out
  // of the viewport while the panel was still open. Fixing them costs the body's top
  // edge as a free anchor: that edge is the header's bottom, measured here because the
  // header wraps to a second line at narrow widths and changes height with the language.
  //
  // The page beneath is left scrollable on purpose. This is a non-modal aside - the
  // dimmed content stays pointer-reachable as the dismiss gesture - and locking it with
  // `overflow: hidden` bought nothing the fixed position had not already bought, while
  // costing a keyboard trap: `overflow: hidden` does not stop scroll chaining, so End
  // with the panel's own region focused still carried the page beneath along, and the
  // reader could then no longer scroll it back. The overlays hold their place either
  // way, which is what the defect was about.
  useEffect(() => {
    if (suspended) return
    registerOverlay(OVERLAY_ID, OVERLAY_LAYER.detailPanel)
    return () => unregisterOverlay(OVERLAY_ID)
  }, [suspended])

  useEffect(() => {
    const shellHeader = document.querySelector('.app-shell-header')
    function measureTop() {
      const bottom = shellHeader?.getBoundingClientRect().bottom ?? 0
      document.documentElement.style.setProperty('--detail-overlay-top', `${bottom}px`)
    }
    measureTop()
    // jsdom has no ResizeObserver; the shell already guards `matchMedia` the same way.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measureTop) : null
    if (shellHeader) observer?.observe(shellHeader)
    window.addEventListener('resize', measureTop)

    function cycleFocus(event: KeyboardEvent) {
      const header = document.querySelector('.app-shell-header')
      // The open Menu section below 768px is a disclosure of the header's toggle and sits
      // on the header disclosures' layer, over this panel, so it joins the cycle after them.
      // Only while the toggle says it is open: at 768 and above the same element is the
      // desktop sidebar, which stays beneath the backdrop and is no place for focus.
      const menu = document.querySelector('#app-shell-nav-toggle[aria-expanded="true"]')
        ? document.querySelector('#app-shell-nav:not([hidden])')
        : null
      // A control an open disclosure paints over is not a place focus can rest.
      const shell = [
        ...([...(header?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])]),
        ...([...(menu?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])]),
      ].filter(unobscured)
      const cycle = [
        ...[...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(unobscured),
        ...shell,
      ]
      if (cycle.length === 0) return
      const active = document.activeElement as HTMLElement | null
      const index = active ? cycle.indexOf(active) : -1
      // Focus can rest on something that is not a stop: a header panel with no control of
      // its own, such as Help, takes focus on open through `tabIndex={-1}` (GRO-66). From
      // there Tab goes on in document order to the next header or Menu stop, and Shift+Tab
      // back to the previous one, as it would with no creative open. With none on that
      // side, the cycle wraps as it always has.
      if (index === -1 && active && active !== document.body) {
        const target = event.shiftKey
          ? [...shell].reverse().find((el) => active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING)
          : shell.find((el) => active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
        if (target) {
          event.preventDefault()
          target.focus()
          return
        }
      }
      const next = event.shiftKey
        ? index <= 0
          ? cycle.length - 1
          : index - 1
        : index === -1 || index === cycle.length - 1
          ? 0
          : index + 1
      event.preventDefault()
      cycle[next].focus()
    }
    function onKeyDown(event: KeyboardEvent) {
      if (suspendedRef.current) return
      if (event.key === 'Escape' && isTopmostOverlay(OVERLAY_ID)) {
        dismissedRef.current = true
        onCloseRef.current()
        return
      }
      // Containment spans the panel and the header for as long as the backdrop exists -
      // including when a header disclosure sits over it, which makes the topmost-overlay
      // condition false while the dimmed controls beneath the backdrop stay real. Only
      // Escape answers to the stack: the topmost overlay closes first.
      if (event.key === 'Tab' && panelRef.current?.isConnected) cycleFocus(event)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', measureTop)
      observer?.disconnect()
      document.documentElement.style.removeProperty('--detail-overlay-top')
    }
  }, [])

  // Focus on open goes to the panel's first control, the close button; on a dismissal, to
  // the recorded trigger, with the shell heading as the fallback when the trigger left the
  // document.
  //
  // A dismissal, and not every unmount. Escape, the backdrop press and the close button
  // are the reader asking for the panel to go, and those restore the trigger wherever
  // focus has wandered to - a header disclosure included, since the header is inside the
  // panel's own Tab cycle. The panel also closes by itself - the client changed, the accounts
  // selection dropped the creative from the list - and then the reader is elsewhere,
  // usually on the very checkbox or radio that caused it, still inside an open
  // disclosure. Pulling focus to the heading there would take them out of a control they
  // are still using, and on a client change it would race that disclosure's own
  // restoration. So the panel restores only what it was asked to.
  useEffect(() => {
    if (!triggerCapturedRef.current) {
      triggerCapturedRef.current = true
      triggerRef.current = document.activeElement as HTMLElement | null
    }
    // The first control focus can actually be seen on, not simply the first one. A
    // disclosure left open while the reader tabbed out to the table is still painted
    // over this panel when a row opens it, and the accounts panel covers exactly where
    // the close button sits; focusing it there puts the focus ring behind something and
    // reads as focus having vanished, which AC3 forbids. The Tab cycle drops those
    // candidates for the same reason and by the same test. With every one of them
    // covered there is nothing better to offer, and the first is taken as before.
    const reachable = [...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])]
    ;(reachable.find(unobscured) ?? reachable[0])?.focus()
    return () => {
      if (!dismissedRef.current) return
      if (triggerRef.current?.isConnected) {
        triggerRef.current.focus()
        return
      }
      // The shell heading is the screen's h1 and the focus contract's fallback: it sits in
      // main, not in the header bar, so the selector follows it rather than the header.
      document.querySelector<HTMLElement>('.app-shell-heading')?.focus()
    }
  }, [])

  const closeButton = (
    <Button size="compact-sm" variant="default" onClick={dismiss} aria-label={t.detailClose}>
      {'✕'}
    </Button>
  )

  const shell = (children: ReactNode) => (
    <>
      <div
        className="detail-backdrop"
        data-testid="detail-backdrop"
        onPointerDown={(event) => {
          // Dismiss on the press, and cancel it: a canceled pointerdown fires no
          // compatibility mouse events, so the release cannot fall through to the row
          // beneath and reopen what the backdrop just dismissed.
          event.preventDefault()
          dismiss()
        }}
        aria-hidden="true"
      />
      <aside ref={panelRef} className="detail-panel" data-testid="detail-panel" aria-label={t.detailAria}>
        <Group justify="space-between" p="sm" style={{ flex: 'none' }}>
          <Text size="sm" fw={700}>{t.detailAria}</Text>
          {closeButton}
        </Group>
        {/* The scrolling region is a keyboard stop of its own. It holds no controls in
            the ready state, so without one the cycle offers only the close button and the split
            table below the fold has no keyboard route at all. `tabindex=0` puts it in
            FOCUSABLE, and therefore in the panel's own Tab cycle. */}
        <div
          className="detail-scroll"
          data-testid="detail-scroll"
          tabIndex={0}
          role="region"
          aria-label={t.detailScroll}
          style={{ overflowY: 'auto', padding: 'var(--mantine-spacing-md)' }}
        >{children}</div>
      </aside>
    </>
  )

  if (state.status === 'loading') {
    return shell(
      <Group gap="sm" data-testid="detail-loading">
        <Loader size="sm" />
        <Text size="sm">{t.loadingCreatives}</Text>
      </Group>,
    )
  }

  if (state.status === 'unknown') {
    return shell(
      <Stack gap="sm" data-testid="detail-not-found" role="alert">
        <Text size="md" fw={700}>{t.notFoundTitle}</Text>
        <Text size="sm" className="grv-muted">{t.notFoundBody}</Text>
        <Group>
          <Button onClick={() => setAttempt((n) => n + 1)}>{t.retry}</Button>
        </Group>
      </Stack>,
    )
  }

  if (state.status === 'error') {
    return shell(
      <Stack gap="sm" data-testid="detail-error" role="alert">
        <Text size="md" fw={700}>{t.loadFailed}</Text>
        <Text size="sm" className="grv-muted">{state.message}</Text>
        <Group>
          <Button
            onClick={() => {
              // The reader's retry starts reconciling afresh.
              reconciled.current = { key: requestKey, moves: 0, listRefreshed: false }
              setAttempt((n) => n + 1)
            }}
          >
            {t.retry}
          </Button>
        </Group>
      </Stack>,
    )
  }

  const { response } = state
  // The echo is the guard's other half: a response answers this request only when its
  // `scope` and `period` echoes both match. The scope object alone is not a sufficient
  // match - it holds no period, so a 14-to-30-day change would leave it untouched while
  // the figures changed. Until both echoes match, the panel keeps its loading state. This
  // is what the echoes are read for, and the only reader they have.
  const accountsEcho = [...response.scope.accountIds].sort().join(',')
  const requestedAccounts =
    params.accountIds === null ? accountsEcho : [...params.accountIds].sort().join(',')
  const answered =
    !listPending &&
    matchesEcho(ask, { period: response.period, comparison: null }, listWindow) &&
    response.scope.audienceId === params.audienceId &&
    accountsEcho === requestedAccounts
  if (!answered) {
    return shell(
      <Group gap="sm" data-testid="detail-loading">
        <Loader size="sm" />
        <Text size="sm">{t.loadingCreatives}</Text>
      </Group>,
    )
  }

  // One row model through the screen's own builder, on the detail's creative against the
  // detail's own benchmarks: the same computation the table shows, read twice. The local
  // context overlays the response's benchmarks and currency, so the verdict and the
  // reliability copy compute entirely within one response, one vintage.
  // The tiers of this response (GRO-186), so the ticks and the copy read the same tiers the
  // detail's verdict was judged with.
  const thresholds = thresholdsOf(response.verdictBasis)
  const row = buildRows([response.creative], thresholds)[0]
  const qualifier = verdictQualifier(row, t)
  const localCtx: ViewContext = {
    ...ctx,
    benchmarks: response.benchmarks,
    currency: response.currency,
    // The detail carries no account list of its own; its split names every account its
    // figures came from, so provenance is read there, within the same response.
    isDemo: response.split.length > 0 && response.split.every((s) => s.account.isDemo),
    // The detail serves its own basis, so a null reference reads the metric of this response.
    basis: response.verdictBasis,
    thresholds,
  }
  const derived = row.derived
  // The audit surface marks every figure from its own membership in the incomplete set -
  // not from one aggregate flag: a spend that every ad reported is complete even when the
  // conversions beside it are not. Ratios keep their derived flags, which already read
  // the completeness of their own inputs.
  const incomplete = row.metrics.incomplete
  const accounts = response.split.map((s) => s.account)
  // The screen's account list first, then the split's: `split=delivered` drops an account
  // whose ads delivered nothing, and those ads are still listed in the block.
  const accountShort = (id: string) =>
    ctx.accounts.find((a) => a.id === id)?.short ?? accounts.find((a) => a.id === id)?.short ?? id
  // The ads of the accounts listed beside the count. `creative.ads` can also hold ads of an
  // account the split leaves out for delivering nothing (`split=delivered`).
  const splitAds = response.split.reduce((sum, s) => sum + s.ads.length, 0)
  // Every mark the panel shows goes through this tally, and its legend reads it last.
  const mark = markTally(t)
  // Duration is a video-only field: an image creative omits the segment rather than
  // letting the no-value glyph stand in for a field it cannot have (evaluation row 9).
  const meta = fill(
    response.creative.durationSeconds === null ? t.detailMetaStill : t.detailMeta,
    {
      k: formatLabel(response.creative.format, t),
      d:
        response.creative.durationSeconds === null
          ? null
          : `${response.creative.durationSeconds}${SECONDS[ctx.lang]}`,
      n: accounts.length,
      a: splitAds,
      p: response.creative.metrics.conversions,
    },
  )

  return shell(
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div data-testid="detail-name" style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
            {response.creative.displayName}
          </div>
          <PausedMark at={response.creative.pausedSince} t={t} lang={ctx.lang} />
          <Text size="xs" className="grv-muted" style={{ marginTop: 2 }} data-testid="detail-meta">
            {meta}
          </Text>
        </div>
        <ConfidenceTicks
          conversions={response.creative.metrics.conversions ?? 0}
          thresholds={localCtx.thresholds}
          aria-label={ticksAria(response.creative.metrics.conversions, localCtx)}
          progress
        />
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div
          className="detail-asset"
          style={{
            aspectRatio: (response.creative.aspectRatio ?? '1:1').replace(':', '/'),
            display: 'flex',
            alignItems: 'flex-end',
            padding: 9,
          }}
        >
          {response.creative.thumbnailUrl ? (
            <img
              src={response.creative.thumbnailUrl}
              loading="lazy"
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Tag size="sm" data-testid="detail-format">
              {formatLabel(response.creative.format, t)}
            </Tag>
          )}
        </div>
        <Stack gap={6}>
          <Group gap="xs" wrap="wrap">
            {[
              labelOf(ctx.dimensions.hypotheses, response.creative.hypothesisId, ctx.lang) ??
                t.hypUnassigned,
              labelOf(ctx.dimensions.techniques, response.creative.techniqueId, ctx.lang) ??
                t.techUnassigned,
            ].map((label, i) => (
              <Text
                key={`dim-${i}`}
                size="sm"
                // `grv-muted` is the design system's muted ink, which clears 4.5:1 in both
                // palettes. The opacity it replaces did not: composited over the light
                // surface the unassigned labels landed near 3.9:1.
                className={label === t.hypUnassigned || label === t.techUnassigned ? 'grv-muted' : undefined}
                data-testid={label === t.hypUnassigned || label === t.techUnassigned ? 'detail-dim-unassigned' : 'detail-dim'}
              >
                {label}
              </Text>
            ))}
            {response.creative.audienceIds.map((aud, i) => (
              <Tag key={aud + i} size="sm">
                {labelOf(ctx.dimensions.audiences, aud, ctx.lang) ?? aud}
              </Tag>
            ))}
          </Group>
        </Stack>
      </SimpleGrid>

      <Divider />
      <Group gap="sm">
        <VerdictMark row={row} t={t} />
        {qualifier !== null && (
          <Text size="sm" data-testid="detail-break">{qualifierText(qualifier)}</Text>
        )}
        <Text component="div" size="sm" data-testid="detail-reason">
          {reasonSentence(row, localCtx)}
        </Text>
      </Group>

      <InsightCard title={reliabilityTitle(row, localCtx.thresholds, t)}>{reliabilityBody(row, localCtx)}</InsightCard>

      <SimpleGrid cols={{ base: 2, xs: 3 }} spacing="sm" data-testid="detail-metrics">
        <MetricCell
          label={t.roasCol}
          value={
            // marked wraps Unrankable, never the reverse: the partial span has to land
            // outside the mark's aria-hidden content, or the partial warning would be
            // hidden from a screen reader exactly where both apply (D7).
            mark.at('cells')(
              floorStruck(ratio(row.derived.roas.value, ctx.lang), row.derived.roas.value !== null, row, localCtx),
              derived.roas.partial,
            )
          }
          sub={
            response.creative.adjustedRoas === null ? null : (
              <>
                {`${t.adj}: `}
                {mark.at('cells')(
                  floorStruck(ratio(response.creative.adjustedRoas, ctx.lang), true, row, localCtx),
                  adjustedPartial(row),
                )}
              </>
            )
          }
        />
        <MetricCell
          label={t.spend}
          value={mark.at('cells')(
            money(row.metrics.spend, response.currency, ctx.lang, 2),
            incomplete.includes('spend'),
          )}
          sub={mark.at('cells')(
            money(row.metrics.conversionValue, response.currency, ctx.lang, 2),
            incomplete.includes('conversionValue'),
          )}
        />
        <MetricCell
          label={t.convCol}
          value={mark.at('cells')(num(row.metrics.conversions, ctx.lang), incomplete.includes('conversions'))}
          sub={mark.at('cells')(
            floorStruck(money(derived.cpa.value, response.currency, ctx.lang, 2), derived.cpa.value !== null, row, localCtx),
            derived.cpa.partial,
          )}
        />
        <MetricCell
          label={t.aovCol}
          value={mark.at('cells')(
            floorStruck(money(derived.aov.value, response.currency, ctx.lang, 2), derived.aov.value !== null, row, localCtx),
            derived.aov.partial,
          )}
        />
        <MetricCell
          label={t.sortCtr}
          value={mark.at('cells')(percent(derived.ctr.value, ctx.lang), derived.ctr.partial)}
          sub={
            <>
              {`${t.clicks.replace('%n', '').trim()}: `}
              {mark.at('cells')(num(row.metrics.clicks, ctx.lang), incomplete.includes('clicks'))}
            </>
          }
        />
        <MetricCell label={t.cpcCol} value={mark.at('cells')(money(derived.cpc.value, response.currency, ctx.lang, 2), derived.cpc.partial)} />
        <MetricCell
          label={t.impressions}
          value={mark.at('cells')(num(row.metrics.impressions, ctx.lang), incomplete.includes('impressions'))}
          sub={mark.at('cells')(money(derived.cpm.value, response.currency, ctx.lang, 2), derived.cpm.partial)}
        />
        <MetricCell
          label={t.adsCol}
          value={num(splitAds, ctx.lang)}
          sub={`${t.account}: ${accounts.length}`}
        />
        <MetricCell
          label={t.share}
          value={share.pct === null ? NO_VALUE : mark.at('cells')(`${num(share.pct, ctx.lang)}%`, share.partial)}
          sub={t.shareSub}
        />
      </SimpleGrid>

      {response.notes.includes('attributionAcrossAccounts') && (
        <Text size="xs" className="grv-muted" data-testid="detail-attribution-note">
          {t.detailAttributionAcross}
        </Text>
      )}
      <Table data-testid="detail-split">
        <Table.Caption className="detail-sub grv-muted">
          {t.splitCaption}
        </Table.Caption>
        <Table.Thead>
          <Table.Tr>
            {/* The wireframe's split headers, not the main table's: the short `t.ads`, and
                a ticks column whose header paints nothing. The name stays in the
                accessibility tree, because a column header with no name is a column a
                screen reader cannot announce (GRO-52). */}
            <Table.Th>{t.account}</Table.Th>
            <Table.Th>{t.ads}</Table.Th>
            <Table.Th>{t.spend}</Table.Th>
            <Table.Th>{t.convCol}</Table.Th>
            <Table.Th>{t.roasCol}</Table.Th>
            <Table.Th>
              <span className="grv-visually-hidden">{t.reliability}</span>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {response.split.map((s) => {
            const rowDerived = derive(s.metrics)
            return (
              <Table.Tr key={s.account.id}>
                <Table.Td>
                  <span>{s.account.name}</span>
                  {/* The list's badge glyph, from the split's own provenance: the panel
                      states what its figures are without borrowing the screen's answer. */}
                  {localCtx.isDemo && (
                    <span title={t.demoMarkLong} data-testid="demo-mark" aria-label={t.demoMarkLong}>
                      {'\u00b0'}
                    </span>
                  )}
                  {mark.account('split', s.account.lagging)}
                  {s.account.portfolioName !== null && !s.account.isDemo && (
                    <div className="detail-sub grv-muted" data-testid="split-portfolio">
                      {s.account.portfolioName}
                    </div>
                  )}
                </Table.Td>
                <Table.Td>{s.ads.length}</Table.Td>
                <Table.Td>
                  <div>{mark.at('cells')(<SplitMoney parts={moneyParts(s.metrics.spend, response.currency, ctx.lang, 2)} />, s.metrics.incomplete.includes('spend'))}</div>
                  <div className="detail-sub">
                    {mark.at('cells')(
                      <SplitMoney parts={moneyParts(s.metrics.conversionValue, response.currency, ctx.lang, 2)} />,
                      s.metrics.incomplete.includes('conversionValue'),
                    )}
                  </div>
                </Table.Td>
                <Table.Td>
                  {/* The mark rides on the cell, not on the number. A null conversions with
                      `conversions` incomplete means nothing was observed *and* something is
                      missing - the unknown null of GRO-60's four states - and short-circuiting
                      it to the bare glyph threw the second half away, leaving it
                      indistinguishable from a measure that simply does not apply. */}
                  {mark.at('cells')(
                    s.metrics.conversions === null ? NO_VALUE : num(s.metrics.conversions, ctx.lang),
                    s.metrics.incomplete.includes('conversions'),
                  )}
                </Table.Td>
                <Table.Td>{mark.at('cells')(ratio(rowDerived.roas.value, ctx.lang), rowDerived.roas.partial)}</Table.Td>
                <Table.Td>
                  <ConfidenceTicks
                    conversions={s.metrics.conversions ?? 0}
                    thresholds={localCtx.thresholds}
                    aria-label={ticksAria(s.metrics.conversions, localCtx)}
                  />
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Th>{fill(t.accMerged, { n: accounts.length })}</Table.Th>
            <Table.Th>{splitAds}</Table.Th>
            <Table.Th>
              {mark.at('cells')(<SplitMoney parts={moneyParts(row.metrics.spend, response.currency, ctx.lang, 2)} />, incomplete.includes('spend'))}
              <div className="detail-sub">
                {mark.at('cells')(
                  <SplitMoney parts={moneyParts(row.metrics.conversionValue, response.currency, ctx.lang, 2)} />,
                  incomplete.includes('conversionValue'),
                )}
              </div>
            </Table.Th>
            <Table.Th>{mark.at('cells')(num(row.metrics.conversions, ctx.lang), incomplete.includes('conversions'))}</Table.Th>
            <Table.Th>{mark.at('cells')(ratio(row.derived.roas.value, ctx.lang), derived.roas.partial)}</Table.Th>
            <Table.Th>
              <ConfidenceTicks
                conversions={row.metrics.conversions ?? 0}
                thresholds={localCtx.thresholds}
                aria-label={ticksAria(row.metrics.conversions, localCtx)}
              />
            </Table.Th>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
      {/* The «Объявления» block (GRO-196): every in-scope ad with its own picture. A server
          that sent no `ads` (before GRO-196, or a rollback) hides the block; read-only, the
          detach control is PRD 0008 phase 2. */}
      {response.ads !== null && (
      <Fragment>
          <Text fw={600} size="sm" data-testid="detail-ads-title">
            {t.adsBlockTitle}
          </Text>
          <Table data-testid="detail-ads">
            <Table.Caption className="detail-sub grv-muted">{t.adsBlockCaption}</Table.Caption>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <span className="grv-visually-hidden">{t.adsBlockTitle}</span>
                </Table.Th>
                <Table.Th>{t.account}</Table.Th>
                <Table.Th>{t.ads}</Table.Th>
                <Table.Th>{t.adStatus}</Table.Th>
                <Table.Th>{t.spend}</Table.Th>
                <Table.Th>{t.adReason}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {response.ads.map((ad) => (
                <Table.Tr key={ad.adId} data-testid="detail-ad">
                  <Table.Td>
                    {ad.thumbnailUrl !== null ? (
                      <img
                        src={ad.thumbnailUrl}
                        loading="lazy"
                        alt=""
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover', display: 'block' }}
                      />
                    ) : null}
                  </Table.Td>
                  <Table.Td>{accountShort(ad.accountId)}</Table.Td>
                  <Table.Td>{ad.name}</Table.Td>
                  <Table.Td>{ad.status ?? NO_VALUE}</Table.Td>
                  <Table.Td>
                    {mark.at('cells')(
                      <SplitMoney parts={moneyParts(ad.spend, response.currency, ctx.lang, 2)} />,
                      ad.spendIncomplete,
                    )}
                  </Table.Td>
                  <Table.Td className="grv-muted">{ad.reason === null ? null : reasonLabel(ad.reason, t)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
      </Fragment>
      )}
      {/* After every cell and the split in source order: the tally has seen each mark the
          panel renders above, the share mark included, by the time this reads it. */}
      <PartialLegend kinds={mark.kinds()} t={t} />
      <Text size="xs" className="grv-muted" data-testid="detail-roas-note">
        {t.roasNote}
      </Text>
      <Text size="xs" className="grv-muted" data-testid="detail-identity">
        {fill(t.identityNote, {
          h: id,
          // The identity is the whole group, so its accounts are those of every ad in it,
          // the ones the split leaves out included, beside names counted the same way.
          n: new Set(response.creative.ads.map((ad) => ad.accountId)).size,
          a: new Set(response.creative.ads.map((ad) => ad.name)).size,
        })}
      </Text>
    </Stack>,
  )
}

/**
 * The reliability copy, chosen in order: first the outcome's completeness, then the band.
 * An outcome measure that is incomplete - or one the creative has no data for at all -
 * parks every ratio resting on it no matter the sample size, so the completeness copy
 * precedes the band copy, which is only reached for a complete outcome.
 */
function reliabilityBody(row: Row, ctx: ViewContext): string {
  const { lang, currency, thresholds } = ctx
  // The count's own availability first, and before any arithmetic. Every band sentence
  // is about the count - n, the distance to the next threshold, the spend the shortfall
  // would cost - so a count the creative does not supply leaves the band nothing to say.
  // Defaulting it to zero read as a reported zero and put `n=0` beside a metrics cell
  // rendering the no-value glyph for the same figure. A null count with a reported
  // purchase value is the case that reached here: the pair is not both-null, so the
  // completeness test below did not catch it on its own.
  if (derivedOutcomeMissing(row)) return fill(ctx.t.relPartial, { m: measureNames(row, ctx) })
  // A stop-loss under the provisional tier keeps the sample-size caveat but is not muted,
  // and it fires on an unmarked null count too, which the verdict serves as n = 0.
  if (row.verdict?.reason === 'stopLoss' && row.verdict.n < thresholds.provisionalAt) {
    return fill(ctx.t.relStopLoss, {
      n: row.verdict.n,
      metric: metricName(verdictMetric(row.verdict, ctx.basis), ctx.t),
      ref: referenceNoun(row.verdict.reference, ctx.t),
    })
  }
  if (row.metrics.conversions === null) return fill(ctx.t.relPartial, { m: measureNames(row, ctx) })
  const conversions = row.metrics.conversions
  if (conversions < thresholds.directionAt) {
    const short = Math.max(thresholds.directionAt - conversions, 0)
    return fill(ctx.t.relIns, {
      n: conversions,
      s: money(row.metrics.spend, currency, lang, 2),
      lo: thresholds.directionAt,
      d: short,
      c: row.derived.cpa.value === null ? null : money(short * row.derived.cpa.value, currency, lang, 2),
    })
  }
  if (row.band === 'insufficient') {
    return fill(ctx.t.relDir, {
      n: conversions,
      lo: thresholds.provisionalAt,
      d: thresholds.provisionalAt - conversions,
    })
  }
  if (row.band === 'provisional') {
    return fill(ctx.t.relProv, { n: conversions, hi: thresholds.reliableAt })
  }
  return fill(ctx.t.relRel, {
    n: conversions,
    ref: referenceNoun(row.verdict?.reference ?? null, ctx.t),
    s: money(row.metrics.spend, currency, lang, 2),
    hi: thresholds.reliableAt,
  })
}

/** The outcome figures are partial, or there are no outcome figures at all. */
/**
 * Is this control reachable where it sits, or is something painted over it? DOM
 * membership cannot answer it: a header control is in the header whether or not an open
 * disclosure covers it. A zero box is not occlusion - an accessibly hidden input has one
 * legitimately - so those pass and the caller's own rules decide.
 *
 * Out of sight is not covered, and conflating the two cost a keyboard user a control. A
 * disclosure taller than the room it has scrolls, and a checkbox below its fold sits
 * where nothing is painted, so a hit test at its centre answered for whatever was there
 * instead and the Tab cycle dropped it. Focusing it is what reveals it - the browser
 * scrolls it in - so the test runs on the part of the control that is actually on
 * screen, and a control with no such part at all stays in the cycle.
 */
function unobscured(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return true
  const box = onScreenBox(el, rect)
  if (box === null) return true
  const at = document.elementFromPoint((box.left + box.right) / 2, (box.top + box.bottom) / 2)
  return Boolean(at && (at === el || el.contains(at) || at.contains(el)))
}

interface Box { top: number; bottom: number; left: number; right: number }

/**
 * The part of this box the reader can see: its rect cut down by every clipping ancestor
 * and by the viewport, or `null` when nothing of it is left.
 *
 * Only an ancestor in the element's containing-block chain clips it. The panel is
 * `position: fixed`, laid out against the viewport, so the scrolling `main` it happens
 * to sit inside in the DOM does not clip it; counting it made the panel's own close
 * button, sitting above `main`'s top edge and genuinely covered by a disclosure, look
 * merely scrolled away. An absolutely positioned element skips static ancestors for the
 * same reason. A transform or a `contain` on an ancestor can make it the containing
 * block of a fixed descendant after all; nothing in this shell does that, and the cost
 * of being wrong there is a focus stop kept rather than one lost.
 */
function onScreenBox(el: HTMLElement, rect: DOMRect): Box | null {
  const box: Box = { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right }
  let position = getComputedStyle(el).position
  for (let parent = el.parentElement; parent && position !== 'fixed'; parent = parent.parentElement) {
    const style = getComputedStyle(parent)
    if (position === 'absolute' && style.position === 'static') continue
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
      const edge = parent.getBoundingClientRect()
      box.top = Math.max(box.top, edge.top)
      box.bottom = Math.min(box.bottom, edge.bottom)
      box.left = Math.max(box.left, edge.left)
      box.right = Math.min(box.right, edge.right)
      if (box.bottom <= box.top || box.right <= box.left) return null
    }
    position = style.position
  }
  box.top = Math.max(box.top, 0)
  box.left = Math.max(box.left, 0)
  box.bottom = Math.min(box.bottom, window.innerHeight)
  box.right = Math.min(box.right, window.innerWidth)
  return box.bottom <= box.top || box.right <= box.left ? null : box
}

function derivedOutcomeMissing(row: Row): boolean {
  return (
    row.derived.outcomePartial ||
    (row.metrics.conversions === null && row.metrics.conversionValue === null)
  )
}

/**
 * The ticks' accessible label. The component's default is `${band} (${conversions})`,
 * which is untranslated and, worse, reads a count the creative never reported as a
 * reported zero: the fixture's a3 split has a null count and was announced as
 * "insufficient (0)" beside a cell rendering the no-value glyph. A count nobody sent
 * supports no band at all, so that case says so instead of naming one.
 */
function ticksAria(conversions: number | null, ctx: ViewContext): string {
  if (conversions === null) return ctx.t.ticksAriaUnknown
  return fill(ctx.t.ticksAria, {
    b: bandTitle(confidenceBand(conversions, ctx.thresholds), ctx.t),
    n: conversions,
  })
}

/** The ticks' three bands, which stay on the provisional and reliable lines. */
function bandTitle(band: Row['band'], t: Dict): string {
  return band === 'reliable' ? t.relReliable : band === 'provisional' ? t.relProvisional : t.relInsufficient
}

/** The card's four ranges: below the ranking floor, direction, provisional, reliable. */
function reliabilityTitle(row: Row, thresholds: ConfidenceThresholds, t: Dict): string {
  const n = row.metrics.conversions
  if (row.band === 'insufficient' && n !== null && n >= thresholds.directionAt) return t.relDirection
  return bandTitle(row.band, t)
}

interface MetricCellProps {
  label: string
  value: ReactNode
  sub?: ReactNode | null
}

/** The provisional label of an ad's membership reason (PRD 0008 rule 9). */
function reasonLabel(reason: MembershipReason, t: Dict): string {
  switch (reason) {
    case 'founder':
      return t.reasonFounder
    case 'exact_bytes':
      return t.reasonExactBytes
    case 'near_exact':
      return t.reasonNearExact
    case 'confirmed_suggestion':
      return t.reasonConfirmedSuggestion
    case 'merged_by_hand':
      return t.reasonMergedByHand
    case 'detached':
      return t.reasonDetached
  }
}

/**
 * A money figure in the account split, allowed to break after a digit group and nowhere
 * else (GRO-89). The cell's `overflow-wrap: anywhere` would otherwise break a Russian
 * figure before its sign; shell.css turns that off for this span from 360 up, and the
 * `<wbr>` between `moneyParts` segments are the only breaks left.
 */
function SplitMoney({ parts }: { parts: string[] }) {
  return (
    <span className="detail-money">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <wbr />}
          {part}
        </Fragment>
      ))}
    </span>
  )
}

function MetricCell({ label, value, sub }: MetricCellProps) {
  return (
    <div>
      <div className="detail-metric-label">{label}</div>
      <div className="detail-metric-value">{value}</div>
      {sub !== null && sub !== undefined && <div className="detail-metric-sub detail-sub">{sub}</div>}
    </div>
  )
}